import axios from 'axios';
import type { APIResponse, RetrieveAllParams } from '~/api/utils';
import { type ComicBookPrint } from '~/types/ComicBook';

const API_URL = '/comic-book/print';

class PrintService {
    async createComicBookPrint(print: ComicBookPrint): Promise<ComicBookPrint> {
        const response = await axios.post<ComicBookPrint>(API_URL, print);
        return response.data;
    }

    async getComicBookPrintById(id: string): Promise<ComicBookPrint> {
        const response = await axios.get<ComicBookPrint>(`${API_URL}/${id}`);
        return response.data;
    }

    async updateComicBookPrint(id: string, updatedComicBookPrint: Partial<ComicBookPrint>): Promise<ComicBookPrint> {
        const response = await axios.put<ComicBookPrint>(`${API_URL}/${id}`, updatedComicBookPrint);
        return response.data;
    }

    async deletePrint(id: string): Promise<void> {
        await axios.delete(`${API_URL}/${id}`);
    }

    async retrieveAllPrints(params?: RetrieveAllParams): Promise<APIResponse<ComicBookPrint>> {
        const response = await axios.get(`${API_URL}`, {
            params: {
                ...params,
            },
        });
        return response.data;
    }

    async generateAssets(id: string, regenerate: boolean): Promise<APIResponse<ComicBookPrint>> {
        return (await axios.post(`${API_URL}/${id}/${regenerate ? 'regenerate' : 'generate'}-assets`))?.data;
    }

    async createCollection(id: string): Promise<APIResponse<ComicBookPrint>> {
        return (await axios.post(`${API_URL}/${id}/create-collection`))?.data;
    }

    async patchPrint(id: string, data: ComicBookPrint): Promise<APIResponse<ComicBookPrint>> {
        return (await axios.patch(`${API_URL}/${id}`, data)).data;
    }

    async approveData(id: string): Promise<void> {
        return (await axios.post(`${API_URL}/${id}/approve-data`))?.data;
    }

    async approveImages(id: string): Promise<void> {
        return (await axios.post(`${API_URL}/${id}/approve-images`))?.data;
    }

    async uploadAssets(id: string): Promise<void> {
        return (await axios.post(`${API_URL}/${id}/upload-assets`))?.data;
    }

    async createCM(id: string): Promise<void> {
        return (await axios.post(`${API_URL}/${id}/create-cm`))?.data;
    }

    async getCM(ids: string[]): Promise<any> {
        return (
            await axios.get(`${API_URL}/cm`, {
                params: {
                    prints: ids.join(','),
                },
            })
        )?.data;
    }

    async getMetadata(id: string[]): Promise<any> {
        return (await axios.get(`${API_URL}/${id}/print-item/stats`))?.data;
    }

    async getPrintItems(id: string[], params?: RetrieveAllParams): Promise<any> {
        return (
            await axios.get(`${API_URL}/${id}/print-item`, {
                params: { ...params },
            })
        )?.data;
    }

    async insertItems(id: string): Promise<any> {
        return (await axios.post(`${API_URL}/${id}/insert-cm-items`))?.data;
    }
}

export default new PrintService();
